import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LogoutOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Col, Form, message, Modal, Row, Space, Spin, Tabs, Tag, Typography } from "antd";
import { InstallationModeContext } from "contexts/InstallationMode/installation-mode-context";
import moment from "moment";
import {
  useGetAssetsAndPositionsQuery,
  usePositionConfigurationByPositionIdsQuery,
  useSmartgrdEmonPowerStatusSubscription,
  useUpdateElectricalConfigurationMutation,
} from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useContext, useEffect, useState } from "react";
import { PositionFunction } from "types/InstallationMode.d";
import useSensorflowLocation from "../../hooks/use-sensorflow-location";
import { ElectricalConfiguration, ElectricalConfigurationInput } from "../../types/electricalConfig";
import {
  formatElectricalConfigForDisplay,
  getDefaultElectricalConfig,
  normalizePhaseType,
  normalizeSensorType,
  validateElectricalConfig,
} from "../../utils/electricalConfig";
import ElectricalConfigForm from "../ElectricalConfig/ElectricalConfigForm";

const { Text } = Typography;

interface MeterInstallationModeModalProps {
  visible: boolean;
  onClose: () => void;
  meterPositionId: string;
  // meterName: string; // Unused prop - commented out
  nodeStatusInfo?: {
    nodeStatus?: string;
    nodeStatusInInstallationMode?: string;
    lastTransmission?: string;
    nodeMacId?: string;
  };
}

interface MeterLiveData {
  lastUpdate?: string;
  channelData?: Array<{
    channel: number;
    measurements: Array<{
      type: string;
      value: number;
      lastUpdated: string;
    }>;
    nodeMacId: string;
    meterType: string;
  }>;
}

const MeterInstallationModeModal: React.FC<MeterInstallationModeModalProps> = ({
  visible,
  onClose,
  meterPositionId,
  // meterName, // Unused variable - commented out
  nodeStatusInfo,
}) => {
  const { handleExitKey, currentKeyEntryOfInstaller, isLoading /* keyName */ } = useContext(InstallationModeContext);
  const { locationId } = useSensorflowLocation();

  const [liveData, setLiveData] = useState<MeterLiveData>({});
  const [lastRefresh, setLastRefresh] = useState<string>(moment().format("HH:mm:ss"));

  // Electrical configuration state
  const [electricalConfigForm] = Form.useForm();
  const [electricalConfig, setElectricalConfig] = useState<ElectricalConfigurationInput>({});
  const [initialElectricalConfig, setInitialElectricalConfig] = useState<ElectricalConfiguration | null>(null);

  // Query to get stream mappings data for resolving metering point position ID
  const { data: assetsAndPositionsData } = useGetAssetsAndPositionsQuery({
    variables: { locationId: locationId || "" },
    skip: !locationId || !meterPositionId,
  });

  // Helper function to resolve the actual metering point position ID from stream mappings
  const resolveMeteringPointPositionId = (subjectPositionId: string): string => {
    if (!assetsAndPositionsData?.sensorflow_stream_mappings) {
      // If no stream mappings data, assume the passed ID is already the metering point
      return subjectPositionId;
    }

    // Check if this position is a subject in stream mappings (i.e., it's a zone/asset being metered)
    const streamMapping = assetsAndPositionsData.sensorflow_stream_mappings.find(
      (mapping: any) => mapping.subjectId === subjectPositionId && !mapping.decommissionedTime
    );

    if (streamMapping) {
      // This is a subject position, return the actual metering point position ID
      return streamMapping.meteringPointPositionId;
    }

    // If not found in stream mappings as a subject, assume it's already the metering point position ID
    return subjectPositionId;
  };

  // Resolve the actual metering point position ID for electrical configuration
  const actualMeteringPointId = meterPositionId ? resolveMeteringPointPositionId(meterPositionId) : null;

  // Helper function to check if this meter is an energy meter (should show electrical config)
  const isEnergyMeter = () => {
    if (!actualMeteringPointId || !assetsAndPositionsData?.positions) return false;

    // Find the metering point position
    const meteringPosition = assetsAndPositionsData.positions.find(
      (pos: any) => pos.positionId === actualMeteringPointId
    );

    if (!meteringPosition || !meteringPosition.nodeSlots || meteringPosition.nodeSlots.length === 0) {
      return false;
    }

    // Check if any node slot has energy node type
    return meteringPosition.nodeSlots.some((slot: any) => {
      // Get the actual node type from the slot mappings
      let actualNodeType = null;
      if (slot.slotMappings && slot.slotMappings.length > 0) {
        const activeMapping = slot.slotMappings.find((mapping: any) => !mapping.decomissionedTime);
        if (activeMapping && activeMapping.node && activeMapping.node.node_type) {
          actualNodeType = activeMapping.node.node_type;
        }
      }

      // Check if this is an energy meter (actual node type or fallback to slot nodeType)
      const nodeType = actualNodeType || slot.nodeType;
      return nodeType === "energy" && !slot.slotRemoveDate;
    });
  };

  // GraphQL mutations and queries for electrical configuration
  const [updateElectricalConfig, { loading: updateElectricalLoading }] = useUpdateElectricalConfigurationMutation({
    onCompleted: () => {
      message.success("Electrical configuration updated successfully");
    },
    onError: (error) => {
      message.error(`Failed to update electrical configuration: ${error.message}`);
    },
  });

  // Query position configuration for electrical settings - use resolved metering point position ID
  const { data: positionConfigData } = usePositionConfigurationByPositionIdsQuery({
    variables: {
      positionIds: actualMeteringPointId ? [actualMeteringPointId] : [],
    },
    skip: !actualMeteringPointId || !visible,
  });

  // Use the real subscription for live meter data - use resolved metering point position ID
  const {
    data,
    loading: dataLoading,
    error: subscriptionError,
  } = useSmartgrdEmonPowerStatusSubscription({
    variables: { meteringPointPositionId: actualMeteringPointId },
    skip: !actualMeteringPointId || !visible,
  });

  // Process the real-time data from the subscription
  useEffect(() => {
    if (data?.smartgrdEmonPowerStatus) {
      const measurements = data.smartgrdEmonPowerStatus;

      if (measurements.length > 0) {
        // Get the most recent update time from measurementTimestamp
        const latestUpdate = measurements
          .map((m) => m.measurementTimestamp)
          .filter(Boolean)
          .sort()
          .pop();

        // Group measurements by channel to show all measurement types per channel
        const groupedByChannel = measurements.reduce((acc, measurement) => {
          const channelIndex = (measurement.channelIndex ?? 0) + 1; // Convert 0-based to 1-based

          if (!acc[channelIndex]) {
            acc[channelIndex] = {
              channel: channelIndex,
              measurements: [],
              nodeMacId: measurement.nodeMacId || "",
              meterType: measurement.meterType || "CT",
            };
          }

          acc[channelIndex].measurements.push({
            type: measurement.measurementType || "energy",
            value: measurement.lastPowerReading || 0,
            lastUpdated: measurement.measurementTimestamp || "",
          });

          return acc;
        }, {} as Record<number, any>);

        const channelData = Object.values(groupedByChannel);

        setLiveData({
          lastUpdate: latestUpdate || moment().format("YYYY-MM-DD HH:mm:ss"),
          channelData,
        });

        setLastRefresh(moment().format("HH:mm:ss"));
      } else {
        // No measurements available
        setLiveData({
          lastUpdate: undefined,
          channelData: [],
        });
        setLastRefresh(moment().format("HH:mm:ss"));
      }
    }
  }, [data]);

  // Load electrical configuration when position config data is available
  useEffect(() => {
    if (positionConfigData?.positionConfigurations?.[0]) {
      const config = positionConfigData.positionConfigurations[0];
      const electricalConfigData: ElectricalConfiguration = {
        voltage: config.voltage || undefined,
        frequency: config.frequency || undefined,
        powerFactor: config.powerFactor ? Number(config.powerFactor) : undefined,
        calibrationFactor: config.calibrationFactor ? Number(config.calibrationFactor) : undefined,
        nominalCurrent: config.nominalCurrent || undefined,
        phaseType: normalizePhaseType(config.phaseType),
        sensorType: normalizeSensorType(config.sensorType),
        primary: config.primary?.toString() || undefined,
        secondary: config.secondary?.toString() || undefined,
      };

      // Filter out undefined values
      const cleanConfig = Object.fromEntries(
        Object.entries(electricalConfigData).filter(([, value]) => value !== undefined && value !== null)
      );

      setInitialElectricalConfig(cleanConfig);
      setElectricalConfig(cleanConfig);
      electricalConfigForm.setFieldsValue(cleanConfig);
    } else {
      // Set default electrical configuration if no position config exists
      const defaultConfig = getDefaultElectricalConfig();
      setInitialElectricalConfig(defaultConfig);
      setElectricalConfig(defaultConfig);
      electricalConfigForm.setFieldsValue(defaultConfig);
    }
  }, [positionConfigData, electricalConfigForm]);

  // Electrical configuration handlers
  const handleElectricalConfigChange = (changedValues: any, allValues: ElectricalConfigurationInput) => {
    setElectricalConfig(allValues);
  };

  const handleSaveElectricalConfig = async () => {
    try {
      const formValues = await electricalConfigForm.validateFields();
      const validation = validateElectricalConfig(formValues);

      if (!validation.isValid) {
        message.error(`Configuration errors: ${validation.errors.join(", ")}`);
        return;
      }

      if (!actualMeteringPointId) {
        message.error("No metering point position found for electrical configuration");
        return;
      }

      await updateElectricalConfig({
        variables: {
          positionIds: [actualMeteringPointId],
          electricalConfig: formValues,
        },
      });

      setInitialElectricalConfig(formValues);
    } catch (configError: any) {
      message.error(`Failed to save electrical configuration: ${configError.message}`);
    }
  };

  const hasElectricalConfigChanged = (): boolean => {
    if (!initialElectricalConfig) return false;
    return JSON.stringify(electricalConfig) !== JSON.stringify(initialElectricalConfig);
  };

  const handleExit = async () => {
    await handleExitKey(onClose);
  };

  const isCurrentMeterInstallation =
    currentKeyEntryOfInstaller?.keyPositionId === actualMeteringPointId &&
    currentKeyEntryOfInstaller?.positionFunction === PositionFunction.METERING_POINT;

  // Show connection status based on data availability
  const isOnline = !!(liveData.channelData && liveData.channelData.length > 0 && liveData.lastUpdate);

  const getStatusIcon = () => {
    if (isOnline) {
      return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "16px" }} />;
    }
    return <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: "16px" }} />;
  };

  const getMeasurementUnit = (type: string): string => {
    if (type === "power") {
      return "W";
    }
    if (type === "consumed_energy" || type === "energy") {
      return "kWh";
    }
    return "";
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ThunderboltOutlined style={{ color: "#1890ff", fontSize: "20px" }} />
          <div>
            <span style={{ fontSize: "18px", fontWeight: 600 }}>CT Meter Installation Mode</span>
            <div style={{ fontSize: "12px", color: "#666", fontWeight: 400, marginTop: 2 }}>
              {getStatusIcon()} Live monitoring active • Last updated: {lastRefresh}
            </div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      bodyStyle={{ padding: "20px" }}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Monitor • Configure • Verify installation before exiting
          </Text>
          {isCurrentMeterInstallation && (
            <Button
              type="primary"
              danger
              size="large"
              icon={<LogoutOutlined />}
              onClick={handleExit}
              loading={isLoading}
            >
              Exit Installation Mode
            </Button>
          )}
        </div>
      }
    >
      <div>
        {subscriptionError && (
          <Alert
            message="Error Loading Measurements"
            description={subscriptionError.message}
            type="error"
            style={{ marginBottom: 16 }}
          />
        )}

        <Tabs
          defaultActiveKey="monitor"
          size="large"
          items={[
            {
              key: "monitor",
              label: (
                <Space>
                  <ThunderboltOutlined />
                  <span>Monitor</span>
                </Space>
              ),
              children: (
                <div style={{ minHeight: 400 }}>
                  {/* Device Status Header */}
                  {nodeStatusInfo && (
                    <div
                      style={{
                        padding: "16px 20px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        marginBottom: 24,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <Text strong style={{ display: "block", marginBottom: 4 }}>
                          {nodeStatusInfo.nodeMacId || "Unknown Device"}
                        </Text>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Last updated: {lastRefresh}
                        </Text>
                      </div>
                      <Tag
                        color={nodeStatusInfo.nodeStatus === "ONLINE" ? "green" : "red"}
                        style={{ fontSize: "13px", fontWeight: 500 }}
                      >
                        {getStatusIcon()} {nodeStatusInfo.nodeStatus || "Unknown"}
                      </Tag>
                    </div>
                  )}

                  <Spin spinning={dataLoading}>
                    {/* Channel Readings */}
                    {liveData.channelData && liveData.channelData.length > 0 ? (
                      <Row gutter={[16, 16]}>
                        {liveData.channelData.map((channel) => (
                          <Col xs={24} sm={12} lg={8} key={channel.channel}>
                            <Card
                              size="small"
                              title={`Channel ${channel.channel}`}
                              style={{ textAlign: "center" }}
                              bodyStyle={{ padding: "16px" }}
                            >
                              {channel.measurements.map((measurement, index) => (
                                <div
                                  key={measurement.type}
                                  style={{ marginBottom: index < channel.measurements.length - 1 ? 12 : 0 }}
                                >
                                  <div style={{ fontSize: "20px", fontWeight: 600, color: "#52c41a" }}>
                                    {measurement.value.toFixed(2)}{" "}
                                    <span style={{ fontSize: "14px", color: "#666" }}>
                                      {getMeasurementUnit(measurement.type)}
                                    </span>
                                  </div>
                                  <Text type="secondary" style={{ fontSize: "11px" }}>
                                    {measurement.type.replace("_", " ").toUpperCase()}
                                  </Text>
                                </div>
                              ))}
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
                        <ThunderboltOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <div style={{ fontSize: "16px", marginBottom: 8 }}>No measurements detected</div>
                        <Text type="secondary">Ensure CT clamps are connected and the device has power</Text>
                      </div>
                    )}
                  </Spin>
                </div>
              ),
            },
            // Only show electrical configuration tab for energy meters
            ...(isEnergyMeter()
              ? [
                  {
                    key: "configure",
                    label: (
                      <Space>
                        <SettingOutlined />
                        <span>Configure</span>
                        {hasElectricalConfigChanged() && <Tag color="orange">Modified</Tag>}
                      </Space>
                    ),
                    children: (
                      <div style={{ minHeight: 400, padding: "0 8px" }}>
                        <div style={{ marginBottom: 20 }}>
                          <Text strong style={{ fontSize: "16px" }}>
                            Electrical Parameters
                          </Text>
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary">
                              Configure voltage, frequency, and other electrical settings for accurate power
                              measurements
                            </Text>
                          </div>
                        </div>

                        <ElectricalConfigForm
                          form={electricalConfigForm}
                          initialValues={electricalConfig}
                          onValuesChange={handleElectricalConfigChange}
                          showAdvanced
                          compact={false}
                          editMode
                        />

                        {Object.keys(electricalConfig).length > 0 && (
                          <Alert
                            message="Current Configuration"
                            description={formatElectricalConfigForDisplay(electricalConfig)}
                            type="info"
                            showIcon
                            style={{ marginTop: 20, marginBottom: 20 }}
                          />
                        )}

                        <div
                          style={{ textAlign: "right", marginTop: 32, paddingTop: 20, borderTop: "1px solid #f0f0f0" }}
                        >
                          <Space>
                            <Button
                              onClick={() => {
                                electricalConfigForm.setFieldsValue(initialElectricalConfig || {});
                                setElectricalConfig(initialElectricalConfig || {});
                              }}
                              disabled={!hasElectricalConfigChanged()}
                            >
                              Reset Changes
                            </Button>
                            <Button
                              type="primary"
                              onClick={handleSaveElectricalConfig}
                              loading={updateElectricalLoading}
                              disabled={!hasElectricalConfigChanged()}
                            >
                              Save Configuration
                            </Button>
                          </Space>
                        </div>
                      </div>
                    ),
                  },
                ]
              : []),
            {
              key: "guide",
              label: (
                <Space>
                  <CheckCircleOutlined />
                  <span>Installation Guide</span>
                </Space>
              ),
              children: (
                <div style={{ minHeight: 400, padding: "0 8px" }}>
                  <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ fontSize: "16px" }}>
                      Installation Checklist
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary">
                        Follow these steps to ensure proper CT meter installation and configuration
                      </Text>
                    </div>
                  </div>

                  <div style={{ lineHeight: 2 }}>
                    {[
                      "Verify CT clamps are properly installed around conductors",
                      "Check power readings are within expected range for each channel",
                      ...(isEnergyMeter()
                        ? ["Configure electrical parameters (voltage, frequency, power factor, etc.)"]
                        : []),
                      "Ensure connection status shows 'Online'",
                      "Monitor each channel for consistent power readings",
                      "Verify node MAC ID matches your physical device",
                      "Exit installation mode when complete and readings are stable",
                    ].map((step, index) => (
                      <div key={index} style={{ display: "flex", alignItems: "flex-start", marginBottom: 16 }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: "#1890ff",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "bold",
                            marginRight: 12,
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        >
                          {index + 1}
                        </div>
                        <span style={{ fontSize: "14px" }}>{step}</span>
                      </div>
                    ))}
                  </div>

                  <Alert
                    message="Need Help?"
                    description="If you encounter issues during installation, check device connections, verify network connectivity, and ensure CT clamps are properly sized for your conductors."
                    type="info"
                    showIcon
                    style={{ marginTop: 32 }}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>
    </Modal>
  );
};

export default MeterInstallationModeModal;
